/**
 * wlApply.js — КУДА ложится окно W/L и как оно считается по кадру. [12.0.49]
 *
 * Чистые правила, без движка: их проверяет tests/wlapply_test.mjs, а
 * исполняет core/viewport.js (applyWindowToSlices, applyAutoWindowToSlices).
 *
 * ПОВОД. Владелец: «быстрые клавиши окна меняют только активное окно снимка, а
 * чипы — все окна срезов — сделай как правильно». Входов у окна было пять, и
 * правила у них разные:
 *   · чипы, ручной W/L, сценарии — во ВСЕ окна срезов, без разбора;
 *   · клавиши 1–5, Shift+1–4 и голос — только в одно окно;
 *   · голос к тому же держал свои числа (кости 1500/400, печень 150/70 —
 *     мимо каталога 2500/480 и 150/30).
 * Ни то, ни другое не верно целиком. «Во все» на ПЭТ/КТ в раскладке 2×2 клало
 * КТ-окно 400/40 и на окно ПЭТ — картинка ПЭТ белела. «В одно» на МПР давало
 * аксиальный срез в лёгочном окне рядом с корональным в мягкотканном — у
 * одного и того же объёма.
 *
 * ПРАВИЛО (одно на все пять входов):
 *   окно ложится на активное окно снимка и на все окна срезов ТОЙ ЖЕ
 *   модальности. Плоскости МПР, соседние серии КТ, прошлая КТ в сравнении —
 *   получают одно окно (так читают снимки: связанное окно у одной модальности).
 *   ПЭТ рядом с КТ, МРТ рядом с КТ — не трогаются: их числа в других единицах.
 * Если модальность активного окна не записана — по-старому во все: отличить
 * не по чему. Окно, чья модальность не записана, — тоже получает: молча
 * пропустить его хуже, чем окно, которое человек видит и поправит.
 *
 * ОКНО ПО КАДРУ (МРТ, ангиография, NM, ПЭТ, маммография) считается для
 * КАЖДОЙ СЕРИИ ОТДЕЛЬНО: T1, T2, FLAIR и DWI в раскладке 2×2 — это четыре
 * разные шкалы, и окно, снятое с T2, на DWI было бы ошибкой. Плоскости одного
 * объёма — одна серия, им окно одно (снятое с активного окна).
 *
 * ЕДИНИЦЫ. Окно накладывается на те же числа, что лежат в массиве кадра:
 * загрузчик (dicom-image-loader 5.6.3, preScale включён по умолчанию) уже
 * умножил их на RescaleSlope и прибавил RescaleIntercept. Прежний расчёт
 * делал это второй раз. На КТ, МРТ Siemens и ангиографии (наклон 1, сдвиг 0)
 * этого не было видно; у ПЭТ наклон дробный, у МРТ Philips — не единица, и
 * окно уезжало в разы. Теперь числа берутся как есть.
 */

/** Доля отбрасываемых хвостов: 1-й и 99-й перцентили (доктрина 10.3.2). */
export const НИЗ = 0.01;
export const ВЕРХ = 0.99;

/**
 * Окно по значениям кадра: от 1-го до 99-го перцентиля.
 *
 * Хвосты отбрасываются ради чёрных полей коллиматора, пересвеченных краёв и
 * впечатанных надписей: по min/max окно растянулось бы на весь диапазон, и
 * ткань вышла бы серой кашей.
 *
 * @param {ArrayLike<number>} px — массив кадра (как его отдаёт getPixelData)
 * @param {{ отсчётов?: number }} [нстр] — сколько точек брать (по умолчанию 250 000)
 * @returns {{ww:number, wc:number}|null} null — кадр пуст или однотонен
 */
export function окноПоЗначениям(px, нстр = {}) {
  if (!px || !px.length) return null;
  const отсчётов = нстр.отсчётов > 0 ? нстр.отсчётов : 250000;
  const шаг = Math.max(1, Math.floor(px.length / отсчётов));
  const з = [];
  for (let i = 0; i < px.length; i += шаг) {
    const v = px[i];
    if (Number.isFinite(v)) з.push(v);
  }
  if (з.length < 16) return null;
  з.sort((a, b) => a - b);
  const lo = з[Math.floor(з.length * НИЗ)];
  const hi = з[Math.min(з.length - 1, Math.floor(з.length * ВЕРХ))];
  if (!(hi > lo)) return null;
  // Целые — для шкал в сотнях и тысячах (КТ, МРТ, ПЭТ в Бк/мл). Узкое окно
  // (дробные единицы) целыми не округляется: 0,6 превратилось бы в 1.
  const ww = hi - lo, wc = (hi + lo) / 2;
  const окр = ww >= 20 ? Math.round : (x) => Math.round(x * 100) / 100;
  return { ww: окр(ww), wc: окр(wc) };
}

const _мод = (м) => String(м || '').trim().toUpperCase();

/**
 * Каким окнам снимка достаётся окно.
 *
 * @param {{id:string, модальность?:string}[]} окна — окна срезов (без 3D)
 * @param {string|null} активное — id активного окна
 * @returns {{ цели: string[], пропущены: {id:string, модальность:string}[], модальность: string }}
 *   модальность — по ней отбирали ('' — не отбирали)
 */
export function целиОкна(окна, активное) {
  const список = Array.isArray(окна) ? окна.filter((о) => о && о.id) : [];
  const акт = список.find((о) => о.id === активное) || null;
  const м = _мод(акт?.модальность);
  if (!м) return { цели: список.map((о) => о.id), пропущены: [], модальность: '' };
  const цели = [], пропущены = [];
  for (const о of список) {
    const мо = _мод(о.модальность);
    if (!мо || мо === м) цели.push(о.id);
    else пропущены.push({ id: о.id, модальность: мо });
  }
  return { цели, пропущены, модальность: м };
}

/**
 * Окна для расчёта по кадру — сгруппированные по серии. Кадр группы — кадр
 * активного окна, если оно в группе; иначе кадр первого окна группы.
 * Окно без записанной серии — группа само по себе (своя шкала неизвестна).
 *
 * @param {{id:string, серия?:string, кадр?:string|null}[]} окна — уже отобранные целиОкна
 * @param {string|null} активное
 * @returns {{ серия: string, ids: string[], кадр: string|null, сАктивным: boolean }[]}
 *   группа с активным окном — первой
 */
export function группыПоСериям(окна, активное) {
  const группы = new Map();
  for (const о of (окна || [])) {
    if (!о || !о.id) continue;
    const ключ = о.серия ? `s:${о.серия}` : `v:${о.id}`;
    if (!группы.has(ключ)) группы.set(ключ, { серия: о.серия || '', ids: [], кадр: null, сАктивным: false });
    const г = группы.get(ключ);
    г.ids.push(о.id);
    if (о.id === активное) { г.сАктивным = true; г.кадр = о.кадр || г.кадр; }
    else if (!г.кадр && !г.сАктивным) г.кадр = о.кадр || null;
  }
  return [...группы.values()].sort((a, b) => Number(b.сАктивным) - Number(a.сАктивным));
}

export default { окноПоЗначениям, целиОкна, группыПоСериям, НИЗ, ВЕРХ };
